// ── BCH Live — Creator Studio ─────────────────────────────────────
//
// One page for everything a creator does around a broadcast:
//
//   • Go live now, or schedule for later (NIP-53 `status: planned`)
//   • Full metadata — title, description, thumbnail, category, tags
//   • Overlay toggles, so their tip/goal overlays match their scene
//   • Live dashboard — viewers, chat, BCH raised, top supporters
//
// The heartbeat publisher is unchanged: it is the single most reliability-
// critical piece of the whole feature, and it's been tuned in production.
// If heartbeats stop, viewers see "offline" after HEARTBEAT_TIMEOUT_SECS.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Radio, Check, ArrowLeft, Loader2, Play, Square, Eye, Share2, Link2,
  ShieldAlert, Calendar, ImagePlus, X, Tag, Coins, Settings2, Users,
  MessageCircle, Trophy, CalendarClock, Trash2, Upload, Sparkles, AlertTriangle,
  ChevronDown, ExternalLink, Clock,
} from 'lucide-react';
import type { Event } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useStreamTips } from '../hooks/useStreamTips';
import { uploadImage } from '../lib/imageUpload';
import { startUnthrottledInterval, type UnthrottledInterval } from '../lib/workerTimer';
import {
  STREAM_EVENT_KIND, LIVE_CHAT_KIND, HEARTBEAT_INTERVAL_MS,
  extractUsername, getStreamInfo, setStreamInfo,
  isValidStreamUrl, detectPlatform, parseStreamEvent,
  slog, swarn, describeStream,
} from '../lib/streamCredentials';
import {
  STREAM_CATEGORIES, buildStreamTags, getStreamDraft, setStreamDraft,
  getOverlayPrefs, setOverlayPrefs, OVERLAY_CONFIGS,
  formatScheduledTime, toLiveStream,
  type StreamDraft, type OverlayPrefs,
} from '../lib/liveStream';
import { formatUsd } from '../components/live/StreamOverlays';

// ══════════════════════════════════════════════════════════════════
//  OWN LIVE STATUS
// ══════════════════════════════════════════════════════════════════

function useCreatorLiveStatus(userPubkey: string | undefined) {
  // Stable `since` — recomputing it every render created a new filter object
  // on each pass, which retriggered the query in a loop.
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 2);
  const filters = useMemo(
    () => (userPubkey ? [{ kinds: [STREAM_EVENT_KIND], authors: [userPubkey], limit: 20, since }] : []),
    [userPubkey, since],
  );
  const { events, loading, refetch } = useNostrQuery({ filters, allRelays: true });

  useEffect(() => {
    if (!userPubkey) return;
    const i = setInterval(refetch, 30_000);
    return () => clearInterval(i);
  }, [userPubkey, refetch]);

  const { stream, scheduled } = useMemo(() => {
    if (!userPubkey || events.length === 0) return { stream: null, scheduled: [] };

    // Collapse to newest per `d` so a scheduled stream and a live one can
    // coexist without one masking the other.
    const byD = new Map<string, Event>();
    for (const ev of events) {
      const d = ev.tags.find(t => t[0] === 'd')?.[1] || '';
      const prev = byD.get(d);
      if (!prev || ev.created_at > prev.created_at) byD.set(d, ev);
    }

    const all = [...byD.values()].map(ev => ({ ev, meta: toLiveStream(parseStreamEvent(ev), ev.tags) }));
    const liveOne = all.find(x => x.meta.isLive);
    const upcoming = all
      .filter(x => x.meta.isUpcoming)
      .sort((a, b) => (a.meta.startsAt || Infinity) - (b.meta.startsAt || Infinity));

    // Fall back to the freshest event so title/URL survive a reload.
    const newest = [...events].sort((a, b) => b.created_at - a.created_at)[0];
    const parsed = liveOne ? liveOne.meta : (newest ? parseStreamEvent(newest) : null);
    slog('studio', `own status from ${events.length} event(s): ${describeStream(parsed)}`);

    return { stream: parsed, scheduled: upcoming.map(x => x.meta) };
  }, [events, userPubkey]);

  return { stream, scheduled, isLive: stream?.isLive || false, loading, refetch };
}

// ══════════════════════════════════════════════════════════════════
//  HEARTBEAT PUBLISHER
//
//  CRITICAL for stream reliability. Publishes via useNostrPublish AND a
//  direct broadcast to extra relays, because the shared relay pool can
//  hold stale connections. On failure it retries fast (5s → 8s → 10s)
//  before returning to the normal 30s cadence.
// ══════════════════════════════════════════════════════════════════

const HEARTBEAT_RELAYS = [
  'wss://relay.bchnostr.com',
  'wss://nos.lol', 'wss://relay.primal.net',
  'wss://relay.nostr.band', 'wss://purplepag.es',
];

/**
 * Re-broadcast an ALREADY-SIGNED event to the extra heartbeat relays.
 *
 * Relays silently drop unsigned events, so the guard below is what keeps
 * this redundancy path from being a silent no-op.
 */
async function publishHeartbeatDirect(event: Event): Promise<boolean> {
  if (!event?.id || !event?.sig) {
    swarn('beat', 'refusing to broadcast an unsigned event (would be dropped by every relay)');
    return false;
  }
  const accepted: string[] = [];
  const rejected: string[] = [];

  const promises = HEARTBEAT_RELAYS.map(url => new Promise<boolean>((resolve) => {
    try {
      const ws = new WebSocket(url);
      const timer = setTimeout(() => {
        try { ws.close(); } catch { /* already closed */ }
        resolve(false);
      }, 5000);

      ws.onopen = () => {
        try {
          ws.send(JSON.stringify(['EVENT', event]));
        } catch {
          clearTimeout(timer);
          ws.close();
          resolve(false);
        }
      };

      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          if (data[0] === 'OK' && data[1] === event.id) {
            clearTimeout(timer);
            ws.close();
            if (data[2] === false) {
              rejected.push(`${url} (${data[3] || 'no reason given'})`);
              resolve(false);
            } else {
              accepted.push(url);
              resolve(true);
            }
          }
        } catch { /* malformed frame */ }
      };

      ws.onerror = () => {
        clearTimeout(timer);
        try { ws.close(); } catch { /* already closed */ }
        resolve(false);
      };
      ws.onclose = () => { clearTimeout(timer); resolve(false); };
    } catch { resolve(false); }
  }));

  const results = await Promise.allSettled(promises);
  if (rejected.length) swarn('beat', 'extra relays REJECTED the heartbeat', rejected);
  slog('beat', `extra broadcast: ${accepted.length}/${HEARTBEAT_RELAYS.length} relay(s) accepted`);
  return results.some(r => r.status === 'fulfilled' && r.value);
}

function useHeartbeat(
  isLive: boolean,
  userPubkey: string | undefined,
  draft: StreamDraft,
) {
  const { publish } = useNostrPublish();
  const workerRef = useRef<UnthrottledInterval | null>(null);
  const mountedRef = useRef(true);

  // Keep the draft in a ref so editing metadata mid-stream doesn't tear
  // down and restart the whole heartbeat loop.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const streamUrl = draft.streamUrl;

  // ── Screen Wake Lock ──────────────────────────────────────────────
  // The heartbeat loop runs in THIS browser tab. If the device sleeps or
  // the browser throttles background timers (very aggressive on mobile),
  // heartbeats stop and viewers see the stream drop offline within
  // HEARTBEAT_TIMEOUT_SECS even though the video feed is fine. A screen
  // wake lock keeps the display awake while live, which also keeps the
  // tab foregrounded and its timers running.
  //
  // Caveats (documented for honesty): the wake lock is auto-released when
  // the tab is backgrounded/hidden, so we re-acquire it on visibility
  // return. It does NOT survive the user switching to another app or
  // closing the tab — nothing browser-side can. It's best-effort and only
  // available over HTTPS in supporting browsers.
  const wakeLockRef = useRef<any>(null);
  useEffect(() => {
    const nav = navigator as any;
    const supported = 'wakeLock' in nav;
    if (!isLive || !userPubkey || !streamUrl || !supported) return;

    let released = false;

    const acquire = async () => {
      try {
        if (wakeLockRef.current) return; // already held
        const lock = await nav.wakeLock.request('screen');
        if (released) { try { await lock.release(); } catch { /* noop */ } return; }
        wakeLockRef.current = lock;
        slog('wakelock', 'screen wake lock acquired — device will stay awake while live');
        lock.addEventListener?.('release', () => {
          // The OS/browser dropped it (e.g. tab hidden). Clear our handle so
          // the visibility handler can re-acquire when the tab returns.
          if (wakeLockRef.current === lock) wakeLockRef.current = null;
          slog('wakelock', 'wake lock released by browser');
        });
      } catch (err) {
        swarn('wakelock', 'could not acquire wake lock — device may sleep and drop the stream', err);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisibility);
      const lock = wakeLockRef.current;
      wakeLockRef.current = null;
      if (lock) { try { lock.release(); } catch { /* noop */ } }
    };
  }, [isLive, userPubkey, streamUrl]);

  useEffect(() => {
    if (!isLive || !userPubkey || !streamUrl) {
      if (workerRef.current) {
        slog('beat', 'stopping heartbeat loop');
        workerRef.current.stop();
        workerRef.current = null;
      }
      if (isLive && userPubkey && !streamUrl) {
        swarn('beat', 'LIVE but no stream URL — heartbeat cannot run, viewers will see you drop offline');
      }
      return;
    }

    let consecutiveFailures = 0;
    let lastSuccessTime = Date.now();
    let beatCount = 0;
    let inFlight = false;
    // Current worker-interval tier (ms). We only rebuild the worker timer
    // when the target delay actually changes, so steady-state healthy
    // streaming keeps a single 30s ticker running.
    let currentDelay = HEARTBEAT_INTERVAL_MS;
    slog('beat', `starting heartbeat loop every ${HEARTBEAT_INTERVAL_MS / 1000}s for ${userPubkey.slice(0, 8)} (worker timer — survives background tabs)`);

    const targetDelay = () =>
      consecutiveFailures === 0 ? HEARTBEAT_INTERVAL_MS
      : consecutiveFailures === 1 ? 5_000
      : consecutiveFailures === 2 ? 8_000
      : 10_000;

    // Rebuild the worker ticker at the given interval. Because the worker
    // runs off the main thread, its ticks are NOT throttled when the tab is
    // hidden — the whole point of this change.
    const arm = (delay: number) => {
      currentDelay = delay;
      if (workerRef.current) { workerRef.current.stop(); workerRef.current = null; }
      workerRef.current = startUnthrottledInterval(delay, () => { void beat(); });
    };

    const beat = async () => {
      // Guard against overlapping runs (a slow publish + a fast retry tier).
      if (inFlight || !mountedRef.current) return;
      inFlight = true;

      const now = Math.floor(Date.now() / 1000);
      const heartbeatEvent = {
        kind: STREAM_EVENT_KIND,
        content: '',
        tags: buildStreamTags({
          pubkey: userPubkey,
          draft: draftRef.current,
          status: 'live',
          now,
        }),
        created_at: now,
      };

      // Sign + publish once via the normal path, then re-broadcast the SAME
      // signed event to the extra relays. Signing happens only once, so the
      // creator's extension isn't prompted twice per beat.
      let succeeded = false;
      beatCount++;
      const t0 = Date.now();
      try {
        const signed = await publish(heartbeatEvent);
        succeeded = true;
        slog('beat', `#${beatCount} published in ${Date.now() - t0}ms (heartbeat=${now}, id=${signed.id.slice(0, 8)})`);
        // Fire-and-forget extra propagation — never blocks the next beat.
        publishHeartbeatDirect(signed).catch(() => {});
      } catch (err) {
        succeeded = false;
        swarn('beat', `#${beatCount} publish FAILED after ${Date.now() - t0}ms`, err);
      } finally {
        inFlight = false;
      }

      if (succeeded) {
        consecutiveFailures = 0;
        lastSuccessTime = Date.now();
      } else {
        consecutiveFailures++;
        swarn('beat', `consecutive failures: ${consecutiveFailures} — retrying faster. If this keeps up, viewers will see you go offline in ~4 min.`);
      }

      if (!mountedRef.current) return;

      // Only rearm the ticker if the delay tier changed (healthy → still 30s).
      const next = targetDelay();
      if (next !== currentDelay) arm(next);
    };

    // Fire an immediate first beat, then start the worker ticker.
    void beat();
    arm(currentDelay);

    // Belt-and-braces: when the tab returns to the foreground, if we somehow
    // haven't beaten recently (e.g. the worker was also suspended on a hard
    // device sleep), fire a catch-up beat immediately.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && mountedRef.current) {
        if (Date.now() - lastSuccessTime > 20_000) void beat();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (workerRef.current) { workerRef.current.stop(); workerRef.current = null; }
    };
  }, [isLive, userPubkey, streamUrl, publish]);
}

// ══════════════════════════════════════════════════════════════════
//  FORM PIECES
// ══════════════════════════════════════════════════════════════════

function Field({
  label, hint, icon: Icon, children,
}: {
  label: string;
  hint?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
        {hint && <span className="font-medium text-muted-foreground/50">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

// ── 24/7 / always-on streaming guide ──────────────────────────────
// Collapsible how-to for running a stream that stays live without a
// browser tab open. This is the correct path for unattended / 24/7
// streams (music channels, etc.): dedicated streaming software publishes
// its OWN NIP-53 heartbeat, so liveness never depends on BCH Nostr's tab.
function AlwaysOnGuide() {
  const [searchParams] = useSearchParams();
  // Auto-expand + scroll into view when arrived via a "?guide=1" deep link
  // (e.g. the "See how to set it up" button on the BCH Live page).
  const [open, setOpen] = useState(() => searchParams.get('guide') === '1');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (searchParams.get('guide') === '1') {
      setOpen(true);
      // Wait for layout, then scroll the card into view.
      const t = setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(t);
    }
  }, [searchParams]);
  return (
    <div ref={ref} className="overflow-hidden rounded-2xl border-2 border-primary/40 bg-primary/5 shadow-sm scroll-mt-4">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-primary/10"
        aria-expanded={open}
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15">
          <Clock className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-extrabold text-foreground">Want a more reliable stream?</p>
            <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary-foreground">
              Recommended
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Stream dropping out, or want to run it 24/7? Set it up so it never depends on this tab. Tap to see how. {open ? '' : '→'}
          </p>
        </div>
        <ChevronDown className={`h-5 w-5 shrink-0 text-primary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="space-y-4 border-t border-primary/20 bg-card px-4 pb-5 pt-4 text-xs leading-relaxed text-muted-foreground">
          <p>
            The <span className="font-semibold text-foreground">Go live</span> button
            above sends your live signal from this browser tab every 30 seconds. That's
            fine for a quick stream you're watching over — but if you close the tab, switch
            apps, or your device sleeps, the stream ends for viewers even if your video is
            still running. That's the usual reason a stream &ldquo;drops&rdquo; after a few
            minutes.
          </p>
          <p>
            For a stream that stays rock-solid — whether you&apos;re streaming for an hour
            or running a 24/7 channel — use dedicated streaming software that publishes its
            own live signal, so it never depends on this tab. Here&apos;s the most popular
            free option:
          </p>

          <div className="space-y-3">
            <h4 className="flex items-center gap-1.5 text-sm font-bold text-foreground">
              <Radio className="h-4 w-4 text-primary" /> Owncast (free, self-hosted)
            </h4>
            <ol className="ml-4 list-decimal space-y-2 marker:font-bold marker:text-primary">
              <li>
                <span className="font-semibold text-foreground">Install Owncast</span> on
                an always-on machine or a cheap VPS (a $5/mo box is plenty for audio /
                low-res video). One-line installer and Docker images at{' '}
                <a href="https://owncast.online/quickstart/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-semibold text-primary hover:underline">
                  owncast.online <ExternalLink className="h-3 w-3" />
                </a>.
              </li>
              <li>
                <span className="font-semibold text-foreground">Point your source at it.</span>{' '}
                Stream into Owncast with OBS (or any RTMP encoder), or have it loop a
                playlist for a music channel. Owncast then serves a public{' '}
                <span className="font-mono text-foreground">.m3u8</span> HLS URL — usually{' '}
                <span className="break-all font-mono text-foreground">https://your-domain/hls/stream.m3u8</span>.
              </li>
              <li>
                <span className="font-semibold text-foreground">Announce it on BCH Nostr.</span>{' '}
                Two ways:
                <ul className="ml-4 mt-1.5 list-disc space-y-1.5">
                  <li>
                    <span className="font-semibold text-foreground">Easiest:</span> paste
                    that <span className="font-mono">.m3u8</span> URL into the stream link
                    above and hit Go live. (Note: this still uses this tab for the live
                    signal, so it's best for attended streams.)
                  </li>
                  <li>
                    <span className="font-semibold text-foreground">True 24/7:</span> use
                    Owncast's Nostr / NIP-53 integration (or a small script) to publish
                    the live event and heartbeat itself. Then your stream stays live on
                    BCH Nostr with no browser open at all — exactly how the always-on
                    channels here do it.
                  </li>
                </ul>
              </li>
            </ol>
          </div>

          <div className="rounded-xl border border-border bg-muted/40 p-3">
            <p className="font-semibold text-foreground">Other always-on options</p>
            <p className="mt-1">
              Any tool that publishes a NIP-53 (kind 30311) live event with its own
              heartbeat works here — e.g. a dedicated RTMP→HLS host plus a tiny heartbeat
              script, or a Nostr-native streaming service. BCH Nostr embeds the player and
              layers chat, Super Chats and BCH tipping on top automatically.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls =
  'w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary';

// ── Thumbnail uploader ────────────────────────────────────────────
function ThumbnailPicker({
  value, onChange,
}: { value: string; onChange: (url: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    setUploading(true);
    setPct(0);
    try {
      const url = await uploadImage(file, setPct);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setPct(0);
    }
  };

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => handleFile(e.target.files?.[0])}
      />

      {value ? (
        <div className="relative overflow-hidden rounded-xl border border-border">
          <img src={value} alt="Stream thumbnail" className="aspect-video w-full object-cover" />
          <div className="absolute right-2 top-2 flex gap-1.5">
            <button
              onClick={() => fileRef.current?.click()}
              className="rounded-lg bg-black/70 p-1.5 text-white backdrop-blur-sm hover:bg-black/85"
              title="Replace thumbnail"
              type="button"
            >
              <Upload className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => onChange('')}
              className="rounded-lg bg-black/70 p-1.5 text-white backdrop-blur-sm hover:bg-red-500"
              title="Remove thumbnail"
              type="button"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border transition-colors hover:border-primary/50 hover:bg-muted/40 disabled:opacity-60"
        >
          {uploading ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-xs font-semibold text-muted-foreground">Uploading… {pct}%</span>
            </>
          ) : (
            <>
              <ImagePlus className="h-6 w-6 text-muted-foreground/50" />
              <span className="text-xs font-bold text-muted-foreground">Add a thumbnail</span>
              <span className="text-[10px] text-muted-foreground/60">16:9 works best</span>
            </>
          )}
        </button>
      )}

      {error && <p className="mt-1.5 text-[11px] font-semibold text-red-500">{error}</p>}
    </div>
  );
}

// ── Tag editor ────────────────────────────────────────────────────
function TagEditor({
  tags, onChange,
}: { tags: string[]; onChange: (t: string[]) => void }) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const clean = draft.trim().replace(/^#/, '').toLowerCase();
    if (!clean) return;
    if (tags.includes(clean) || tags.length >= 6) { setDraft(''); return; }
    onChange([...tags, clean]);
    setDraft('');
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
          }}
          placeholder="Add a tag and press Enter"
          maxLength={24}
          className={inputCls}
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim() || tags.length >= 6}
          className="shrink-0 rounded-xl bg-muted px-4 text-xs font-bold text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
        >
          Add
        </button>
      </div>

      {tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {tags.map(t => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-[11px] font-bold text-primary"
            >
              #{t}
              <button
                type="button"
                onClick={() => onChange(tags.filter(x => x !== t))}
                aria-label={`Remove tag ${t}`}
                className="rounded hover:text-red-500"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <p className="mt-1.5 text-[10px] text-muted-foreground">
        Up to 6 tags. These help viewers find your stream.
      </p>
    </div>
  );
}

// ── Category picker ───────────────────────────────────────────────
function CategoryPicker({
  value, onChange,
}: { value: string; onChange: (id: string) => void }) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {STREAM_CATEGORIES.map(c => {
        const active = value === c.id;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(active ? '' : c.id)}
            className={`relative overflow-hidden rounded-xl border p-2.5 text-left transition-all ${
              active
                ? 'border-primary ring-2 ring-primary/30'
                : 'border-border hover:border-primary/40'
            }`}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${c.gradient} ${active ? 'opacity-25' : 'opacity-10'}`} />
            <span className="relative block truncate text-[11px] font-bold">{c.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  LIVE DASHBOARD
// ══════════════════════════════════════════════════════════════════

function LiveDashboard({ pubkey, aTag }: { pubkey: string; aTag: string }) {
  const { supporters, totalUsd, tips } = useStreamTips(pubkey);
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400);

  const { events: chatEvents } = useNostrQuery({
    filters: aTag ? [{ kinds: [LIVE_CHAT_KIND], '#a': [aTag], limit: 300, since }] : [],
    enabled: !!aTag,
  });

  const uniqueChatters = useMemo(
    () => new Set(chatEvents.map(e => e.pubkey)).size,
    [chatEvents],
  );

  const stats = [
    { icon: MessageCircle, label: 'Messages', value: String(chatEvents.length), accent: 'text-blue-500' },
    { icon: Users, label: 'Chatters', value: String(uniqueChatters), accent: 'text-violet-500' },
    { icon: Coins, label: 'BCH raised', value: formatUsd(totalUsd), accent: 'text-emerald-500' },
    { icon: Trophy, label: 'Supporters', value: String(supporters.length), accent: 'text-amber-500' },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {stats.map(s => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-3 text-center">
            <s.icon className={`mx-auto mb-1 h-4 w-4 ${s.accent}`} />
            <p className="text-base font-extrabold tabular-nums">{s.value}</p>
            <p className="text-[10px] font-semibold text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {tips.length > 0 && (
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3">
          <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
            Latest support
          </p>
          <p className="text-xs text-muted-foreground">
            {tips.length} tip{tips.length === 1 ? '' : 's'} totalling{' '}
            <span className="font-extrabold text-emerald-500">{formatUsd(totalUsd)}</span>
          </p>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PAGE
// ══════════════════════════════════════════════════════════════════

export default function CreatorStudioPage() {
  const { user } = useAppContext();
  const { profile } = useNostrProfile(user?.pubkey);
  const username = extractUsername(profile?.nip05);
  const { publish } = useNostrPublish();

  const { stream, scheduled, isLive: relayIsLive, refetch } = useCreatorLiveStatus(user?.pubkey);

  // Local override: immediately force offline when "End Stream" is clicked,
  // without waiting for a relay round-trip. This stops the heartbeat instantly
  // so it can't overwrite the "ended" event with another "live" beat.
  const [forcedOffline, setForcedOffline] = useState(false);
  const isLive = forcedOffline ? false : relayIsLive;

  // ── Draft state, seeded from the saved draft then legacy stream info ──
  const [draft, setDraft] = useState<StreamDraft>(() => {
    const pk = user?.pubkey || '';
    const saved = pk ? getStreamDraft(pk) : null;
    if (saved) return saved;
    const legacy = pk ? getStreamInfo(pk) : { title: '', description: '', streamUrl: '' };
    return {
      title: legacy.title,
      description: legacy.description,
      streamUrl: legacy.streamUrl,
      image: '',
      categoryId: '',
      topics: [],
      startsAt: 0,
    };
  });

  const patch = useCallback((p: Partial<StreamDraft>) => {
    setDraft(d => ({ ...d, ...p }));
  }, []);

  // Persist the draft so a reload mid-setup doesn't lose everything.
  useEffect(() => {
    if (user?.pubkey) setStreamDraft(user.pubkey, draft);
  }, [user?.pubkey, draft]);

  // While live, fall back to whatever is already on the live event so a page
  // reload mid-stream can't blank out title/description/URL.
  const liveDraft = useMemo<StreamDraft>(() => ({
    ...draft,
    title: draft.title || stream?.title || '',
    description: draft.description || stream?.summary || '',
    streamUrl: draft.streamUrl || stream?.streamingUrl || '',
  }), [draft, stream]);

  useHeartbeat(isLive, user?.pubkey, liveDraft);

  const [mode, setMode] = useState<'now' | 'schedule'>('now');
  const [scheduleAt, setScheduleAt] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [ending, setEnding] = useState(false);
  const [published, setPublished] = useState<'live' | 'scheduled' | null>(null);
  const [showOverlays, setShowOverlays] = useState(false);
  const [prefs, setPrefs] = useState<OverlayPrefs>(() => getOverlayPrefs(user?.pubkey || ''));

  useEffect(() => {
    if (user?.pubkey) setPrefs(getOverlayPrefs(user.pubkey));
  }, [user?.pubkey]);

  const savePrefs = (next: OverlayPrefs) => {
    setPrefs(next);
    if (user?.pubkey) setOverlayPrefs(user.pubkey, next);
  };

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    }).catch(() => {});
  };

  const trimmedUrl = draft.streamUrl.trim();
  const urlValid = !!trimmedUrl && isValidStreamUrl(trimmedUrl);
  const detectedPlatform = trimmedUrl ? detectPlatform(trimmedUrl) : null;

  const scheduleUnix = useMemo(() => {
    if (!scheduleAt) return 0;
    const t = new Date(scheduleAt).getTime();
    return Number.isFinite(t) ? Math.floor(t / 1000) : 0;
  }, [scheduleAt]);

  const scheduleValid = scheduleUnix > Math.floor(Date.now() / 1000);
  const canGoLive = urlValid && mode === 'now';
  const canSchedule = mode === 'schedule' && scheduleValid && !!draft.title.trim();

  const watchPath = username ? `/live/${username}` : `/live/${user ? `pk/${user.pubkey}` : ''}`;

  // ── Go Live ────────────────────────────────────────────────────
  const handleGoLive = async () => {
    if (!user?.pubkey || !canGoLive) return;
    setPublishing(true);
    setForcedOffline(false);
    try {
      const plat = detectPlatform(trimmedUrl);
      slog('golive', `going live — platform=${plat.platform} embeddable=${plat.embedUrl ? 'YES' : 'NO'} url=${trimmedUrl}`);
      if (!plat.embedUrl && plat.platform !== 'hls') {
        swarn('golive', 'this URL cannot be embedded — viewers will only get an "Open Stream" link, not an inline player');
      }

      // Keep the legacy store in sync so older code paths still resolve.
      setStreamInfo(user.pubkey, {
        title: draft.title,
        description: draft.description,
        streamUrl: trimmedUrl,
      });

      const now = Math.floor(Date.now() / 1000);
      const goLiveDraft: StreamDraft = { ...draft, streamUrl: trimmedUrl, startsAt: now };

      await publish({
        kind: STREAM_EVENT_KIND,
        content: '',
        tags: buildStreamTags({ pubkey: user.pubkey, draft: goLiveDraft, status: 'live', now }),
        created_at: now,
      });

      // Announce to followers.
      const watchUrl = username
        ? `https://bchnostr.com/live/${username}`
        : `https://bchnostr.com/live/pk/${user.pubkey}`;

      await publish({
        kind: 1,
        content: `I'm live on BCH Nostr!\n\n${draft.title || 'BCH Live Stream'}${draft.description ? '\n' + draft.description : ''}\n\nWatch: ${watchUrl}\n\n#bch #bitcoincash #bch-live`,
        tags: [['t', 'bch'], ['t', 'bitcoincash'], ['t', 'bch-live']],
        created_at: now + 1,
      });

      slog('golive', `published kind ${STREAM_EVENT_KIND} (d=${user.pubkey.slice(0, 8)}) + announcement note`);
      setPublished('live');
      setTimeout(() => { setPublished(null); refetch(); }, 3000);
    } catch (err) {
      swarn('golive', 'GO LIVE FAILED', err);
    } finally {
      setPublishing(false);
    }
  };

  // ── Schedule ───────────────────────────────────────────────────
  const handleSchedule = async () => {
    if (!user?.pubkey || !canSchedule) return;
    setPublishing(true);
    try {
      const now = Math.floor(Date.now() / 1000);
      // Scheduled streams get their own `d` coordinate so they don't clobber
      // the creator's main live slot (d == pubkey).
      const dTag = `${user.pubkey}:sched:${scheduleUnix}`;
      const schedDraft: StreamDraft = { ...draft, streamUrl: trimmedUrl, startsAt: scheduleUnix };

      await publish({
        kind: STREAM_EVENT_KIND,
        content: '',
        tags: buildStreamTags({ pubkey: user.pubkey, draft: schedDraft, status: 'planned', now, dTag }),
        created_at: now,
      });

      await publish({
        kind: 1,
        content: `📅 Scheduled a BCH Live stream: ${draft.title}\n\n${formatScheduledTime(scheduleUnix)}\n\nhttps://bchnostr.com${watchPath}\n\n#bch #bitcoincash #bch-live`,
        tags: [['t', 'bch'], ['t', 'bitcoincash'], ['t', 'bch-live']],
        created_at: now + 1,
      });

      slog('schedule', `scheduled stream for ${scheduleUnix} (d=${dTag})`);
      setPublished('scheduled');
      setTimeout(() => { setPublished(null); refetch(); }, 3000);
    } catch (err) {
      swarn('schedule', 'SCHEDULE FAILED', err);
    } finally {
      setPublishing(false);
    }
  };

  // ── Cancel a scheduled stream ──────────────────────────────────
  const handleCancelScheduled = async (dTag: string, title: string) => {
    if (!user?.pubkey) return;
    try {
      const now = Math.floor(Date.now() / 1000);
      await publish({
        kind: STREAM_EVENT_KIND,
        content: '',
        tags: [
          ['d', dTag],
          ['title', title],
          ['status', 'ended'],
          ['ends', String(now)],
          ['t', 'bch'], ['t', 'bch-live'],
          ['p', user.pubkey, '', 'Host'],
        ],
        created_at: now,
      });
      slog('schedule', `cancelled scheduled stream d=${dTag}`);
      setTimeout(refetch, 1500);
    } catch (err) {
      swarn('schedule', 'cancel failed', err);
    }
  };

  // ── End Stream ─────────────────────────────────────────────────
  const handleEndStream = async () => {
    if (!user?.pubkey) return;
    setEnding(true);
    // IMMEDIATELY stop the heartbeat and flip the UI offline. Waiting for the
    // relay would let one more "live" beat race past the "ended" event.
    setForcedOffline(true);
    slog('endstream', 'ending stream — heartbeat stopped, publishing status=ended');
    try {
      const now = Math.floor(Date.now() / 1000);
      const endTags = (ts: number) => [
        ['d', user.pubkey],
        ['title', draft.title || stream?.title || 'BCH Live'],
        ['status', 'ended'],
        ['ends', String(ts)],
        ['t', 'bch'], ['t', 'bch-live'],
        ['p', user.pubkey, '', 'Host'],
      ];

      await publish({ kind: STREAM_EVENT_KIND, content: '', tags: endTags(now), created_at: now });
      // A second "ended" event as insurance: it overwrites any stale heartbeat
      // that raced through while the first was in flight.
      await publish({ kind: STREAM_EVENT_KIND, content: '', tags: endTags(now + 1), created_at: now + 1 });

      slog('endstream', 'both "ended" events published — viewers should drop within seconds');
      setTimeout(refetch, 2000);
    } catch (err) {
      swarn('endstream', 'END STREAM FAILED — you may still appear live to viewers', err);
    } finally {
      setEnding(false);
    }
  };

  // ── Guard ──────────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="space-y-4 text-center">
          <ShieldAlert className="mx-auto h-12 w-12 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">Log in to go live.</p>
        </div>
      </div>
    );
  }

  const aTag = stream?.aTag || `${STREAM_EVENT_KIND}:${user.pubkey}:${user.pubkey}`;

  return (
    <div className="pb-10">
      {/* ── Header ── */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/85 px-4 py-3 backdrop-blur-md">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/live" aria-label="Back to BCH Live" className="-ml-1 rounded-lg p-1 hover:bg-muted">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="flex items-center gap-2 text-lg font-extrabold">
              <Radio className="h-5 w-5 text-red-500" /> Creator Studio
            </h1>
            {isLive && (
              <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> LIVE
              </span>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <button
              onClick={() => setShowOverlays(true)}
              title="Stream overlays"
              aria-label="Stream overlay settings"
              className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Settings2 className="h-4 w-4" />
            </button>
            {isLive && (
              <Link
                to={watchPath}
                title="Watch your stream"
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted"
              >
                <Eye className="h-5 w-5" />
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-lg space-y-4 p-4">
        {isLive ? (
          /* ══════════════ LIVE ══════════════ */
          <>
            <div className="space-y-3 rounded-2xl border-2 border-red-500/30 bg-red-500/5 p-5 text-center">
              <div className="flex items-center justify-center gap-2">
                <span className="h-3 w-3 animate-pulse rounded-full bg-red-500" />
                <h2 className="text-lg font-extrabold text-red-500">You&apos;re live!</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Viewers can watch at{' '}
                <Link to={watchPath} className="font-semibold text-primary hover:underline">
                  bchnostr.com{watchPath}
                </Link>
              </p>
              <div className="flex gap-2">
                <Link
                  to={watchPath}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border px-4 py-3 text-sm font-semibold transition-colors hover:bg-muted"
                >
                  <Eye className="h-4 w-4" /> Watch
                </Link>
                <button
                  onClick={handleEndStream}
                  disabled={ending}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-red-600 disabled:opacity-60"
                >
                  {ending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
                  {ending ? 'Ending…' : 'End stream'}
                </button>
              </div>
            </div>

            {/* Keep-alive guidance — the "I'm live" heartbeat runs in THIS tab.
                If it stops, viewers see the stream drop even if the video is
                fine. This is the #1 cause of "my stream ended after a few
                minutes" reports. */}
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <div className="space-y-1 text-xs leading-relaxed text-muted-foreground">
                <p className="font-semibold text-foreground">Keep this tab open to stay live</p>
                <p>
                  Your live signal is sent from this page every 30 seconds. If you
                  close or switch away from this tab, lock your phone, or let the
                  screen sleep, viewers will see your stream end within a few
                  minutes — even if your video is still running.
                </p>
                <p>
                  For a 24/7 or unattended stream, use dedicated streaming software
                  (e.g. Owncast or an always-on server) that publishes its own live
                  status, rather than this page.
                </p>
              </div>
            </div>

            <button
              onClick={() => copyText(`https://bchnostr.com${watchPath}`, 'link')}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-border px-4 py-3 text-sm font-semibold transition-colors hover:bg-muted"
            >
              {copied === 'link'
                ? <><Check className="h-4 w-4 text-emerald-500" /> Link copied!</>
                : <><Share2 className="h-4 w-4" /> Share stream link</>}
            </button>

            <div>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                This stream
              </h3>
              <LiveDashboard pubkey={user.pubkey} aTag={aTag} />
            </div>
          </>
        ) : (
          /* ══════════════ OFFLINE / SETUP ══════════════ */
          <>
            {/* Mode switch */}
            <div className="flex gap-1 rounded-xl bg-muted p-1">
              {([
                ['now', 'Go live now', Play],
                ['schedule', 'Schedule', CalendarClock],
              ] as const).map(([id, label, Icon]) => (
                <button
                  key={id}
                  onClick={() => setMode(id)}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold transition-colors ${
                    mode === id
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>

            {/* 24/7 / always-on streaming how-to — prominent, near the top so
                music-channel / unattended streamers see it before setup. */}
            <AlwaysOnGuide />

            <Field label="Stream title">
              <input
                value={draft.title}
                onChange={e => patch({ title: e.target.value })}
                placeholder="What are you streaming?"
                maxLength={120}
                className={inputCls}
              />
            </Field>

            <Field label="Description" hint="(optional)">
              <textarea
                value={draft.description}
                onChange={e => patch({ description: e.target.value })}
                placeholder="Tell viewers what to expect"
                rows={3}
                maxLength={500}
                className={`${inputCls} resize-none`}
              />
            </Field>

            <Field label="Thumbnail" hint="(optional)" icon={ImagePlus}>
              <ThumbnailPicker value={draft.image} onChange={url => patch({ image: url })} />
            </Field>

            <Field label="Category" hint="(optional)">
              <CategoryPicker value={draft.categoryId} onChange={id => patch({ categoryId: id })} />
            </Field>

            <Field label="Tags" hint="(optional)" icon={Tag}>
              <TagEditor tags={draft.topics} onChange={topics => patch({ topics })} />
            </Field>

            {mode === 'schedule' && (
              <Field label="Starts at" icon={Calendar}>
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={e => setScheduleAt(e.target.value)}
                  className={inputCls}
                />
                {scheduleAt && !scheduleValid && (
                  <p className="mt-1.5 text-[11px] font-semibold text-amber-500">
                    Pick a time in the future.
                  </p>
                )}
                {scheduleValid && (
                  <p className="mt-1.5 text-[11px] font-semibold text-primary">
                    {formatScheduledTime(scheduleUnix)} — viewers will see a countdown.
                  </p>
                )}
              </Field>
            )}

            <Field
              label="Stream link"
              hint={mode === 'schedule' ? '(can be added later)' : undefined}
              icon={Link2}
            >
              <input
                value={draft.streamUrl}
                onChange={e => patch({ streamUrl: e.target.value })}
                placeholder="https://twitch.tv/your-channel"
                className={`${inputCls} ${
                  draft.streamUrl && !urlValid ? 'border-red-500/50' : ''
                }`}
              />

              {detectedPlatform && detectedPlatform.platform !== 'unknown' && urlValid && (
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className="rounded px-2 py-0.5 text-[10px] font-bold text-white"
                    style={{ backgroundColor: detectedPlatform.color }}
                  >
                    {detectedPlatform.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    detected — we&apos;ll embed their player
                  </span>
                </div>
              )}

              <p className="mt-1.5 text-[10px] text-muted-foreground">
                Paste your Twitch, YouTube, Kick, Rumble, Owncast or HLS URL.
              </p>
            </Field>

            {/* Primary action */}
            {mode === 'now' ? (
              <button
                onClick={handleGoLive}
                disabled={publishing || !!published || !canGoLive}
                className={`flex w-full items-center justify-center gap-2.5 rounded-2xl px-4 py-4 text-base font-bold transition-all disabled:opacity-60 ${
                  published === 'live'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-red-500 text-white hover:bg-red-600 active:scale-[0.98]'
                }`}
              >
                {publishing ? <><Loader2 className="h-5 w-5 animate-spin" /> Publishing…</>
                  : published === 'live' ? <><Check className="h-5 w-5" /> Live! Followers notified</>
                  : <><Play className="h-5 w-5" /> Go live</>}
              </button>
            ) : null}
            {mode === 'now' && !published && (
              <p className="flex items-start gap-1.5 px-1 text-[11px] leading-relaxed text-muted-foreground">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                <span>
                  Keep this page open while you stream — your live status is sent
                  from here. Closing the tab or letting your screen sleep ends the
                  stream for viewers.
                </span>
              </p>
            )}
            {mode !== 'now' && (
              <button
                onClick={handleSchedule}
                disabled={publishing || !!published || !canSchedule}
                className={`flex w-full items-center justify-center gap-2.5 rounded-2xl px-4 py-4 text-base font-bold transition-all disabled:opacity-60 ${
                  published === 'scheduled'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]'
                }`}
              >
                {publishing ? <><Loader2 className="h-5 w-5 animate-spin" /> Scheduling…</>
                  : published === 'scheduled' ? <><Check className="h-5 w-5" /> Scheduled!</>
                  : <><CalendarClock className="h-5 w-5" /> Schedule stream</>}
              </button>
            )}

            <p className="text-center text-[11px] text-muted-foreground">
              Your stream lives at{' '}
              <span className="font-semibold text-primary">bchnostr.com{watchPath}</span>
            </p>

            {/* Scheduled list */}
            {scheduled.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-4">
                <h3 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" /> Your scheduled streams
                </h3>
                <div className="space-y-1">
                  {scheduled.map(s => (
                    <div key={s.aTag} className="flex items-center gap-2 rounded-xl p-2 hover:bg-muted/50">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-bold">{s.title}</p>
                        <p className="truncate text-[10px] text-muted-foreground">
                          {formatScheduledTime(s.startsAt)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCancelScheduled(s.dTag, s.title)}
                        title="Cancel this scheduled stream"
                        aria-label={`Cancel ${s.title}`}
                        className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Monetization nudge */}
            <div className="space-y-2 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-4">
              <h3 className="flex items-center gap-1.5 text-xs font-extrabold text-emerald-600 dark:text-emerald-400">
                <Sparkles className="h-3.5 w-3.5" /> Get paid in Bitcoin Cash
              </h3>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                Super Chats and tips land in your wallet instantly — no payout
                delays, no chargebacks. Set a donation goal and viewers will see
                a live progress bar over your stream.
              </p>
              <Link
                to="/support"
                className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 hover:underline dark:text-emerald-400"
              >
                <Coins className="h-3 w-3" /> Set up goals &amp; memberships
              </Link>
            </div>
          </>
        )}

        <div className="border-t border-border pt-3">
          <p className="text-center text-[10px] text-muted-foreground/60">
            Stream from any platform. We embed their player and add BCH tipping
            plus Nostr chat on top.
          </p>
        </div>
      </div>

      {/* ── Overlay settings ── */}
      {showOverlays && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          onClick={() => setShowOverlays(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="sc-modal-enter relative w-full max-w-sm overflow-hidden rounded-t-3xl border border-border bg-card shadow-2xl sm:rounded-3xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="flex items-center gap-2 text-sm font-extrabold">
                <Settings2 className="h-4 w-4 text-primary" /> Stream overlays
              </h2>
              <button
                onClick={() => setShowOverlays(false)}
                aria-label="Close"
                className="rounded-full p-1.5 hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="px-5 pt-3 text-[11px] leading-relaxed text-muted-foreground">
              Choose what appears over your stream. Toggle each one on or off.
            </p>
            <div className="safe-area-bottom space-y-1 p-3">
              {OVERLAY_CONFIGS.map(c => (
                <label
                  key={c.id}
                  className="flex cursor-pointer items-start gap-3 rounded-xl p-2.5 transition-colors hover:bg-muted/60"
                >
                  <input
                    type="checkbox"
                    checked={prefs[c.id]}
                    onChange={e => savePrefs({ ...prefs, [c.id]: e.target.checked })}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-500"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-bold">{c.label}</span>
                    <span className="block text-[11px] text-muted-foreground">{c.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
